import { X, Mail, Phone, MapPin, Briefcase, Award, Star, GraduationCap, Calendar } from 'lucide-react';
import Button from '@components/common/Button';
import Avatar from '@components/common/Avatar';

const MentorProfileModal = ({ mentor, isOpen, onClose, onAskQuestion }) => {
  if (!isOpen || !mentor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl sm:text-2xl font-bold text-primary-700">Mentor Profile</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Profile Header */}
          <div className="flex flex-col items-center mb-6">
            <div className="mb-4">
              <Avatar
                src={mentor.profilePicture}
                name={`${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.name}
                size="2xl"
                showBorder={false}
                className="w-24 h-24 sm:w-32 sm:h-32"
              />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-primary-700 mb-1">
              {mentor.firstName && mentor.lastName 
                ? `${mentor.firstName} ${mentor.lastName}` 
                : mentor.name || 'Mentor'}
            </h3>
            <p className="text-sm sm:text-base text-primary-600 mb-3">
              {mentor.expertise?.[0]?.jobTitle || mentor.field || 'Expert Mentor'}
            </p>
            
            {/* Rating */}
            <div className="flex items-center gap-2 bg-yellow-50 px-4 py-2 rounded-full">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="text-lg font-bold text-primary-700">
                {mentor.rating || 'N/A'}
              </span>
              <span className="text-sm text-gray-600">
                ({mentor.totalReviews || 0} reviews)
              </span>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {mentor.email && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-600 mb-1">Email</p>
                  <p className="text-sm font-medium text-primary-700 break-all">
                    {mentor.email}
                  </p>
                </div>
              </div>
            )}

            {mentor.phone && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-600 mb-1">Phone</p>
                  <p className="text-sm font-medium text-primary-700">
                    {mentor.phone}
                  </p>
                </div>
              </div>
            )}

            {mentor.location && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-600 mb-1">Location</p>
                  <p className="text-sm font-medium text-primary-700">
                    {mentor.location}
                  </p>
                </div>
              </div>
            )}

            {mentor.experience && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Briefcase className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-600 mb-1">Experience</p>
                  <p className="text-sm font-medium text-primary-700">
                    {mentor.experience}
                  </p>
                </div>
              </div>
            )}

            {mentor.qualification && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <GraduationCap className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-600 mb-1">Qualification</p>
                  <p className="text-sm font-medium text-primary-700">
                    {mentor.qualification}
                  </p>
                </div>
              </div>
            )}

            {mentor.joinedDate && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-600 mb-1">Member Since</p>
                  <p className="text-sm font-medium text-primary-700">
                    {new Date(mentor.joinedDate).toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Bio/About */}
          {mentor.bio && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-primary-700 mb-3">About</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                {mentor.bio}
              </p>
            </div>
          )}

          {/* Specializations */}
          {mentor.specializations && mentor.specializations.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-primary-700 mb-3">Specializations</h4>
              <div className="flex flex-wrap gap-2">
                {mentor.specializations.map((spec, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                onAskQuestion(mentor);
                onClose();
              }}
            >
              Ask Question
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorProfileModal;
